import React from 'react'
import PropTypes from 'prop-types'
import { Link } from '@hub/link'
import { useToken } from '@hub/design-system-base'
import classNames from 'classnames'
import { Heading, Paragraph } from '@scentregroup/ui'
import ResponsiveImage from '../responsive-image'
import { WhatsOnItem, WhatsOnEvent } from '@scentregroup/shared/types/whats-on'
import Trackable from '../trackable'
import { trackingActions, trackingTypes } from '../../constants'
import MemberExclusiveLabel from '../member-exclusive-label'
import { isMemberExclusive } from '../../helpers/is-member-exclusive'
import styles from './whats-on-list-item.module.scss'
import { Button } from '@hub/button'

const isEvent = (item: WhatsOnItem): item is WhatsOnEvent =>
  Boolean((item as WhatsOnEvent).time)
const isBookable = (item: WhatsOnItem): item is WhatsOnEvent =>
  Boolean((item as WhatsOnEvent).bookingUrl)

export interface WhatsOnListItemProps {
  item: WhatsOnItem
}

const WhatsOnListItem = ({
  item,
}: WhatsOnListItemProps): React.ReactElement => {
  const textPrimary = useToken('colors', 'textPrimary')
  return (
    <Trackable
      {...item.analyticsMetadata}
      trackingAction={trackingActions.viewPromotion}
      type={trackingTypes.promotion}
    >
      <li className={classNames(styles.item, styles.evolved)}>
        <div className={styles.infoSection}>
          <Link
            href={item.slug}
            sx={{
              flex: '0 0 1',
              display: 'flex',
              textDecoration: 'none',
              color: textPrimary,
            }}
          >
            <div className={styles.info}>
              <Heading type="extrasmall" className={styles.heading}>
                {item.name}
              </Heading>
              <Paragraph type="medium" className={styles.details}>
                {isEvent(item) ? item.time : item.dates}
              </Paragraph>
              <Paragraph type="medium" className={styles.details}>
                {isEvent(item) && item.price ? `${item.price} • ` : ''}
                {item.location}
              </Paragraph>
              <MemberExclusiveLabel
                sx={{
                  width: '91px',
                  marginTop: 'spacing-md',
                }}
                display={isMemberExclusive(item.highlightAttribute)}
                option="small"
              />
              <></>
            </div>
          </Link>
          {isBookable(item) ? (
            <div className={styles.extraActions}>
              <Paragraph type="medium">
                <Button
                  as={Link}
                  href={item.bookingUrl}
                  colorScheme="surfacePrimaryWithText"
                  sx={{
                    lineHeight: 'none',
                    height: 'size-xs',
                    fontSize: 'font-small',
                    paddingY: 'spacing-sm',
                    marginY: 'spacing-md',
                    '&:hover': {
                      textDecoration: 'none',
                    },
                  }}
                >
                  Book now
                </Button>
              </Paragraph>
            </div>
          ) : undefined}
        </div>
        <Link
          href={item.slug}
          sx={{
            display: 'block',
            position: 'relative',
            flexGrow: '0',
            flexShrink: '0',
            flexBasis: ['size-12', 'size-12', 'size-15'],
            textDecoration: 'none',
          }}
        >
          <ResponsiveImage
            alt={item.image.description}
            desktopImageUrl={item.image.sourceDesktop}
            mobileImageUrl={item.image.sourceMobile}
            mobileRatio="square"
            desktopRatio="square"
            numberOfItems={3}
          />
        </Link>
      </li>
    </Trackable>
  )
}

const ImagePropType = PropTypes.shape({
  sourceDesktop: PropTypes.string.isRequired,
  sourceMobile: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
})

export const EventPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  bookingUrl: PropTypes.string,
  image: ImagePropType.isRequired,
})

export const OfferPropType = PropTypes.shape({
  name: PropTypes.string.isRequired,
  dates: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  image: ImagePropType.isRequired,
})

export const ItemPropType = PropTypes.oneOfType([EventPropType, OfferPropType])

WhatsOnListItem.propTypes = {
  item: ItemPropType.isRequired,
}

export default WhatsOnListItem
